import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const USER_SEARCH_RESOURCE = "user" as const;
export const USER_SEARCH_OPERATION = "search" as const;
export const USER_SEARCH_API_PATH = `/v1/${USER_SEARCH_RESOURCE}/${USER_SEARCH_OPERATION}` as const;

export type UserSearchParameters = paths[typeof USER_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type UserSearchBody = paths[typeof USER_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type UserSearchResponse =
  paths[typeof USER_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useUserSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<UserSearchParameters, UserSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<UserSearchResponse, Error, UserSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<UserSearchParameters, UserSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      USER_SEARCH_RESOURCE,
      USER_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<UserSearchResponse>(
        USER_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
